"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Clock, CircleQuestionMark as HelpCircle, LifeBuoy, Loader as Loader2, Mail, Phone, ShieldCheck, Wrench } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const supportHighlights = [{
  icon: Clock,
  title: "Rapid Technical Response",
  description: "Priority triage for production-critical laboratory systems and PAT instrumentation."
}, {
  icon: ShieldCheck,
  title: "Qualified Engineers",
  description: "Certified field service engineers covering regulated facilities across Ireland and the UK."
}, {
  icon: Wrench,
  title: "Lifecycle Maintenance",
  description: "Comprehensive qualification, recalibration, preventative maintenance, and spare parts."
}];
function SupportContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-sm">
        <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-secondary text-primary">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground">
          Request Received
        </h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          {successMessage}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          A support specialist will review your details and contact you promptly.
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Card className="border-border bg-card shadow-md">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2 text-primary">
          <LifeBuoy className="size-5" />
          <span className="text-xs font-semibold tracking-wider uppercase">Technical Support</span>
        </div>
        <CardTitle className="font-default text-2xl font-bold text-foreground">
          Contact Us - Antech Solutions
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Submit your equipment or application support request and our technical service team will respond.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField control={control} name="name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="work_email" rules={{
            required: "Work email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Work Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField control={control} name="company_name" render={({
            field
          }) => <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Corp" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone_number" render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="message" rules={{
          required: "Message is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Describe the instrument issue, maintenance requirement, or support inquiry..." className="min-h-[120px] resize-y" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
              {submitError}
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full font-medium" size="lg">
            {isSubmitting ? <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Submitting Request...
              </> : "Send Support Request"}
          </Button>
        </Form>
      </CardContent>
    </Card>;
}
export default function SupportContact() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="supportcontact" data-nav="light" className="relative bg-background py-16 md:py-24">
      <div className="page-container">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12">
          {/* Left Column: Direct info & context */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
              <HelpCircle className="size-3.5 text-primary" />
              <span>Dedicated Technical Assistance</span>
            </div>

            <h2 className="font-default mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Support &amp; Technical Services
            </h2>

            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Application-led analytical technology, qualification and lifecycle support for regulated laboratories and manufacturing teams across Ireland and the UK.
            </p>

            <div className="mt-8 space-y-6">
              {supportHighlights.map((item, i) => {
              const Icon = item.icon;
              return <div key={item.title} data-index={i} className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                      <Icon className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-default text-base font-semibold text-foreground">
                        {item.title}
                      </h4>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="mt-8 rounded-xl border border-border bg-muted/40 p-5">
              <h4 className="font-default text-sm font-semibold text-foreground">
                Direct Emergency &amp; Routine Enquiries
              </h4>
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Mail className="size-4 text-primary" />
                  <span>IE: info@antech.ie · UK: info@antechsolutions.co.uk</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="size-4 text-primary" />
                  <span>UK: +44 (0)20 346 898 13 / IRL: +353 (0)51 392 391</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Support Form */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <FormProvider formId="contact_form" sectionName="support-contact-section" defaultValues={{
            name: "",
            work_email: "",
            company_name: "",
            phone_number: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            work_email: {
              required: true,
              pattern: "email"
            },
            company_name: {
              required: false
            },
            phone_number: {
              required: false
            },
            message: {
              required: true
            }
          }} submitText="Send Support Request" successMessage="Thank you for reaching out to Antech Solutions support. Your request has been logged successfully.">
              <SupportContactForm />
            </FormProvider>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}